import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { MatPillComponent } from '../../../../shared/components/mat-pill/mat-pill.component';
import { ELoadStatus } from '../../../../state/state.interface';
import { DateUtilitiesService } from '../../../../utilities/date-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { IChanges } from '../items-history/items-history.interface';
import {
  EGuideHistoryType,
  IBeforeAfter,
  IWorkInstructionHistory,
  IWorkInstructionHistoryTableColumns,
  TWorkInstructionValue,
} from './work-instruction-history.interface';
import { IWorkInstructionHistoryState, WorkInstructionHistoryStore } from './work-instruction-history.store';

@Component({
  imports: [
    MatDialogContent,
    MatDialogTitle,
    ReactiveFormsModule,
    MatButton,
    MatDialogClose,
    TranslatePipe,
    MatDialogActions,
    DatatableComponent,
    MatCard,
    MatCardContent,
    MatIcon,
    NgForOf,
    MatPillComponent,
    NgIf,
    NgClass,
  ],
  providers: [WorkInstructionHistoryStore, HttpUtilitiesService],
  selector: 'app-work-instruction-history',
  styleUrl: './work-instruction-history.component.scss',
  templateUrl: './work-instruction-history.component.html',
})
export class WorkInstructionHistoryComponent
  extends BaseCrudPageComponent<IWorkInstructionHistory, IWorkInstructionHistoryState>
  implements OnInit
{
  public datatableConfigurations: IDatatableConfiguration<IWorkInstructionHistoryTableColumns> = {
    addScope: ETableCrudScope.none,
    areCheckboxesEnabled: false,
    deleteScope: ETableCrudScope.none,
    editScope: ETableCrudScope.none,
    isSearchEnabled: false,
  };

  public readonly datatableColumns: IDatatableColumn<IWorkInstructionHistoryTableColumns>[] = [
    { id: 'id', isSortable: false },
    { id: 'workInstructionStep', isSortable: true },
    { id: 'user', isSortable: true },
    { id: 'actionTakenAt', isSortable: true },
    { id: 'changedValues', isSortable: false },
  ];
  public workInstructionHistory: IWorkInstructionHistoryTableColumns[] = [];
  public noneText: string = this.translate.instant('field.none');
  public completed: string = this.translate.instant('workInstructionStepper.status.completed');

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: { itemId: number; workInstructionId: number },
    public readonly store: WorkInstructionHistoryStore,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfWorkInstructionHistory',
      multiContext: 'field.workInstructionHistory',
      nameProperty: 'id',
      singleContext: 'field.workInstructionHistory',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      }).subscribe(({ datatable }) => {
        datatable.sort.active = this.mapSortActiveField(datatable.sort.active);

        if (this.data.itemId && this.data.workInstructionId) {
          this.store.loadWorkInstructionHistory({
            fields: ['before', 'after', 'type', 'createdAt', 'createdByUser.name'],
            filters: [
              { field: 'itemId', ids: [this.data.itemId] },
              { field: 'guideId', ids: [this.data.workInstructionId] },
            ],
            join: ['createdByUser', 'guideStep'],
            limit: datatable.limit,
            page: datatable.page,
            sort: [datatable.sort],
            ...(datatable.search
              ? { search: { searchedFields: ['name', 'key'], searchText: datatable.search ?? '' } }
              : {}),
          });
        }
      }),
    );

    this.store.state$.subscribe((state) => {
      if (state.dataLoading === ELoadStatus.success) {
        this.workInstructionHistory = state.data.map((data: IWorkInstructionHistory) => {
          return {
            actionTakenAt: DateUtilitiesService.convertUTCToUserFormatted(data.createdAt, true),
            changedValues: this.getChanges(data.before, data.after),
            id: data.id,
            user: data.createdByUser?.name,
            workInstructionStep: data.guideStep?.name,
          };
        });

        this.workInstructionHistory = this.workInstructionHistory.filter(
          (item: IWorkInstructionHistoryTableColumns) => item.changedValues && item.changedValues.length > 0,
        );
      }
    });
  }

  private getChanges(before: IBeforeAfter | null, after: IBeforeAfter | null, isFormElement = false): IChanges[] {
    const keys: Set<string> = new Set<string>();
    const changes: IChanges[] = [];

    if (before) {
      Object.keys(before).forEach((key: string) => keys.add(key));
    }

    if (after) {
      Object.keys(after).forEach((key: string) => keys.add(key));
    }

    keys.forEach((key) => {
      if (key === 'formData') {
        changes.push(...this.handleFormDataChange(before, after, key));
      } else if (this.shouldAddChange(before, after, key)) {
        changes.push(this.buildChange(before, after, isFormElement, key));
      }
    });

    return changes;
  }

  private handleFormDataChange(before: IBeforeAfter | null, after: IBeforeAfter | null, key: string): IChanges[] {
    const beforeValue = (before?.[key]?.value ?? {}) as IBeforeAfter;
    const afterValue = (after?.[key]?.value ?? {}) as IBeforeAfter;

    return this.getChanges(beforeValue, afterValue, true);
  }

  private shouldAddChange(before: IBeforeAfter | null, after: IBeforeAfter | null, key: string): boolean {
    const beforeValue: IBeforeAfter | string | null = before?.[key]?.value || null;
    const afterValue: IBeforeAfter | string | null = after?.[key]?.value || null;

    const isEmpty = (val: IBeforeAfter | string | null): boolean =>
      val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0);

    const isBeforeValid: boolean = isEmpty(beforeValue);
    const isAfterValid: boolean = isEmpty(afterValue);

    return (!isBeforeValid || !isAfterValid) && beforeValue !== afterValue;
  }

  private buildChange(
    before: IBeforeAfter | null,
    after: IBeforeAfter | null,
    isFormElement: boolean,
    key: string,
  ): IChanges {
    return {
      after: this.formatAndTranslateValue(key, after?.[key] ?? null),
      before: this.formatAndTranslateValue(key, before?.[key] ?? null),
      field: this.getFieldName(key, before?.[key]?.name, after?.[key]?.name, isFormElement),
      key,
    };
  }

  private formatAndTranslateValue(key: string, field: TWorkInstructionValue | null): string {
    const value: string | undefined = field?.value as string;

    if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
      return this.noneText;
    }

    if (field?.type === EGuideHistoryType.date) {
      return DateUtilitiesService.convertUTCToUserFormatted(value, true) ?? this.noneText;
    }

    if (key === 'status') {
      return this.translate.instant(`workInstructionStepper.status.${value}`);
    }

    if (key.includes('checkbox')) {
      return this.translate.instant(`field.${value ? 'checked' : 'unchecked'}`);
    }

    if (key.includes('textArea')) {
      const doc: Document = new DOMParser().parseFromString(value, 'text/html');

      return Boolean(doc.body.textContent) ? String(doc.body.textContent) : this.noneText;
    }

    return value.toString();
  }

  private getFieldName(key: string, beforeName?: string, afterName?: string, isFormElement = false): string {
    if (isFormElement) {
      return afterName ?? beforeName ?? key;
    }

    if (key === 'guide') {
      return this.translate.instant('field.workInstruction');
    }

    return this.translate.instant(`field.${key}`);
  }

  private mapSortActiveField(field: string): string {
    const fieldMap: Record<string, string> = {
      actionTakenAt: 'createdAt',
      user: 'createdByUser.name',
      workInstructionStep: 'guideStep.name',
    };

    return fieldMap[field] || field;
  }
}
